import { computed, ref } from 'vue';
import { useComponentI18n } from '@rdrive/i18n';
import { tokens, useChartTheme } from '@rdrive/theme';
import { downloadOptionAsPNG } from '@rdrive/helper-chart-export';
import messages from './messages/index.js';
import { measureStatusBreakdownCard, buildStatusBreakdownOption } from '../../helpers/statusBreakdownPng.js';

// Chart configuration. The bars' SVG width: the card's 400 px default less its padding.
const totalBars = 50;
const chartHeight = 40;
const chartWidth = 400 - 2 * 24; // subtract padding

// Every opt-in below resolves to "nothing" when it is off — no prop, no listener, no class — so a
// card that passes none renders exactly what it did before them.
const NONE = Object.freeze({});

export default (props, { emit }) => {
  // LOCAL composer with the warn flags off (@rdrive/i18n) — the empty state's `noData`
  const { t } = useComponentI18n(messages);

  const focusedItemId = ref(null);

  let focusClearTimeout = 0;
  function focusSegment(itemId) {
    clearTimeout(focusClearTimeout);
    focusedItemId.value = itemId;
  }
  function blurSegments() {
    focusClearTimeout = setTimeout(() => (focusedItemId.value = null), 100);
  }

  const total = computed(() => props.items.reduce((sum, n) => sum + n.value, 0));

  function adjustForChart(array, item, index, { length }) {
    const percent = (100 * item.value) / total.value;
    const barsPercentRatio = 100 / totalBars;
    return [
      ...array,
      {
        ...item,
        // `selectable` dims by opacity instead (segmentOpacity below), so its colours stay
        color:
          props.selectable || focusedItemId.value === null || focusedItemId.value === item.id
            ? item.color
            : tokens.chart.statusBreakdown.dimmed,
        value:
          index < length - 1
            ? Math.floor(percent / barsPercentRatio) * barsPercentRatio
            : 100 - array.reduce((sum, n) => sum + n.value, 0),
      },
    ];
  }

  // `selectable` / `selectedId` (opt-in, AM_ABSORPTION_PLAN step C2): standalone
  // asset-management's click-to-filter status card. The parent owns the selection; the card only
  // reports a click and shows `selectedId`.
  const hasSelection = () => props.selectedId !== null && props.selectedId !== undefined;
  const isSelected = (item) => hasSelection() && item.id === props.selectedId;
  const select = (item) => {
    if (props.selectable) emit('select', item);
  };
  // Standalone AM's `barOpacity`, in its precedence: a hovered/focused legend item dims every
  // other segment; else an active selection dims every unselected one.
  const segmentOpacity = (item) => {
    if (focusedItemId.value !== null) return focusedItemId.value === item.id ? 1 : tokens.chart.dimOpacity;
    if (hasSelection()) return isSelected(item) ? 1 : tokens.chart.dimOpacity;
    return 1;
  };
  const selectableSegment = (segment, item) => ({
    ...segment,
    style: { ...segment.style, opacity: segmentOpacity(item) },
    onClick: () => select(item),
  });

  const segments = computed(() => {
    if (!props.items.length) return [];

    const drawn = props.items.filter((item) => item.value > 0); // Filter out items with 0 value
    return drawn
      .reduce((array, item, index, arr) => adjustForChart(array, item, index, arr), [])
      .map((item, index) => {
        // Ensure at least 2 bars for v-sparkline
        const barCount = Math.max(2, Math.round((totalBars * item.value) / 100));
        const segment = {
          color: item.color,
          modelValue: Array.from({ length: barCount }, () => 1),
          width: Math.round((chartWidth * item.value) / 100),
          height: chartHeight,
          lineWidth: 5,
          smooth: 2,
          max: 1,
          style: {
            width: `${item.value}%`,
          },
        };
        // the click reports the item as the parent gave it (`drawn[index]`, not the bar maths' copy)
        return props.selectable ? selectableSegment(segment, drawn[index]) : segment;
      });
  });

  // The card root: the namespace class style.css hangs the segments' pointer + transition on.
  const cardAttrs = computed(() => (
    props.selectable ? { class: 'rdrive-status-breakdown-card--selectable' } : NONE
  ));

  // A legend item, while selectable: a clickable v-list-item — Vuetify then gives it its link
  // states (ripple, hover overlay) and its own Enter/Space handling (a key dispatches a click), so
  // no keydown listener here; the template's tabindex="0" stays (Vuetify's own would be -2).
  // Vuetify renders the legend v-list as role="listbox", so an item is an "option" whose
  // aria-selected carries the toggle state; `active` is Vuetify's selected look (AM's).
  const legendAttrs = (item) => {
    if (!props.selectable) return NONE;
    const selected = isSelected(item);
    return {
      active: selected,
      role: 'option',
      'aria-selected': selected ? 'true' : 'false',
      onClick: () => select(item),
    };
  };

  // --- PNG export (opt-in `exportFilename`) --------------------------------------------
  // The card as it looks on screen (../../helpers/statusBreakdownPng.js): its live geometry —
  // measured from `rootEl`, a STRING template ref on the root <div> — redrawn in the theme's
  // colours, every status in full colour (no hover/selection dimming)
  const rootEl = ref(null);
  const theme = useChartTheme();
  const drawnItems = computed(() => props.items.filter((item) => item.value > 0));
  const hasDrawn = computed(() => drawnItems.value.length > 0);

  const downloadPng = () => {
    if (!props.exportFilename || !hasDrawn.value) return;
    const layout = measureStatusBreakdownCard(rootEl.value);
    if (!layout) return;
    const mode = theme.value.mode;
    const { option, width, height } = buildStatusBreakdownOption({
      layout,
      segments: segments.value,
      items: drawnItems.value,
      title: props.exportTitle || props.title,
      mode,
    });
    // no legend and no title band: the option is the card itself
    downloadOptionAsPNG(option, { filename: props.exportFilename, width, height, mode, legend: false });
  };

  return {
    t,
    rootEl,
    hasDrawn,
    downloadPng,
    segments,
    cardAttrs,
    legendAttrs,
    focusSegment,
    blurSegments,
  };
};
